import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Loader2,
  CheckCircle2,
  Mail,
  Send,
  Monitor,
  Smartphone,
  Edit3,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import mjml2html from "mjml-browser";
import { colors, fonts, surfaces } from "../../styles/theme";
import { useAuth } from "../../hooks/useAuth";
import { useTenant } from "../../contexts/TenantContext";
import { Button } from "../../components/ui";
import EmailEditor from "../../components/EmailEditor";
import { getWebcastInviteTemplate } from "../../data/templates";
import {
  fetchContacts,
  fetchMyAllowedSendAs,
  sendZoomInvitations,
  fetchAudienceLists,
  resolveAudienceList,
} from "../../services/api";
import { getGraphToken } from "../../services/msal";
import { inputStyle, labelStyle } from "./constants";

const TYPE_FILTERS = [
  { id: "all", label: "All" },
  { id: "client", label: "Clients", color: "#10b981" },
  { id: "prospect", label: "Prospects", color: "#f59e0b" },
  { id: "subscriber", label: "Subscribers", color: "#6366f1" },
  { id: "media", label: "Media", color: "#ec4899" },
];

// Merge tags available for webcast invitations
const INVITE_MERGE_TAGS = [
  { tag: "{{first_name}}", label: "First Name", example: "Sarah" },
  { tag: "{{last_name}}", label: "Last Name", example: "Chen" },
  { tag: "{{company}}", label: "Company", example: "Meridian Capital" },
  {
    tag: "{{registration_link}}",
    label: "Registration Link",
    example: "#register",
  },
];

export default function InviteContactsModal({ event, onClose, onInvited }) {
  const { user } = useAuth();
  const tenant = useTenant();
  const editorRef = useRef(null);
  const iframeRef = useRef(null);

  // Step: "design" or "send"
  const [step, setStep] = useState("design");

  // Editor state
  const template = getWebcastInviteTemplate(tenant, event);
  const [subject, setSubject] = useState(template.subject);
  const [previewMode, setPreviewMode] = useState(null);

  // Contact/send state
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sendMethod, setSendMethod] = useState("sendgrid");
  const [senderEmail, setSenderEmail] = useState("");
  const [allowedSendAs, setAllowedSendAs] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [audienceLists, setAudienceLists] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Load audience lists on mount
  useEffect(() => {
    fetchAudienceLists()
      .then((data) => setAudienceLists(data.lists || []))
      .catch(() => {});
  }, []);

  const handleLoadList = async (listId) => {
    if (!listId) return;
    setLoadingList(true);
    try {
      const data = await resolveAudienceList(listId);
      const resolvedEmails = new Set((data.contacts || []).map((c) => c.email.toLowerCase()));
      // Match resolved contacts against loaded contacts
      const matchedIds = contacts.filter((c) => resolvedEmails.has((c.email || "").toLowerCase())).map((c) => c.id);
      setSelected(new Set(matchedIds));
    } catch (err) {
      setError(`Failed to load list: ${err.message}`);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (
      sendMethod === "o365" ||
      sendMethod === "draft" ||
      sendMethod === "hybrid"
    ) {
      setLoadingDelegates(true);
      fetchMyAllowedSendAs()
        .then((data) => {
          setAllowedSendAs(data.allowedEmails || []);
          if (data.allowedEmails?.length > 0 && !senderEmail) {
            setSenderEmail(data.allowedEmails[0]);
          }
        })
        .catch(() => setAllowedSendAs([]))
        .finally(() => setLoadingDelegates(false));
    }
  }, [sendMethod]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(
      () => {
        fetchContacts({ search, limit: 200 })
          .then((data) => {
            if (!cancelled) setContacts(data.contacts || []);
          })
          .catch(() => {})
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      search ? 300 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const filtered = contacts.filter((c) => {
    if (filterType === "all") return true;
    const tags = (c.tags || []).map((t) => t.toLowerCase());
    if (filterType === "client") return tags.some((t) => t.includes("client"));
    if (filterType === "prospect")
      return tags.some(
        (t) =>
          t.includes("prospect") ||
          t.includes("opportunity") ||
          t.includes("interest"),
      );
    if (filterType === "media")
      return tags.some((t) => t.includes("press") || t.includes("media"));
    return true;
  });

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((c) => c.id)));
    }
  };

  // Compile MJML to HTML for preview
  const getCompiledEmailHtml = useCallback(() => {
    const bodyHtml = editorRef.current?.getHTML() || "";
    // Replace merge tags with example values for preview
    let preview = bodyHtml;
    INVITE_MERGE_TAGS.forEach((mt) => {
      const cleanTag = mt.tag.replace(/[{}]/g, "");
      const spanPattern = new RegExp(
        `<span[^>]*data-merge-tag="${cleanTag}"[^>]*>[^<]*</span>`,
        "gi",
      );
      preview = preview.replace(spanPattern, mt.example);
      preview = preview.replace(
        new RegExp(mt.tag.replace(/[{}]/g, "\\$&"), "g"),
        mt.example,
      );
    });
    preview = preview.replace(
      /<img /g,
      '<img style="max-width:100%;height:auto;display:block;" ',
    );
    const mjmlString = template.mjml.replace("{{BODY}}", preview);
    try {
      const { html } = mjml2html(mjmlString, { validationLevel: "soft" });
      return html;
    } catch (err) {
      return `<html><body style="color:red;padding:20px;">MJML Error: ${err.message}</body></html>`;
    }
  }, [template.mjml]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (doc?.body) iframe.style.height = doc.body.scrollHeight + 20 + "px";
    } catch {
      /* cross-origin safety */
    }
  }, []);

  // Compile final HTML for sending (with merge tag placeholders preserved)
  const compileFinalHtml = useCallback(() => {
    const bodyHtml = editorRef.current?.getHTML() || "";
    const mjmlString = template.mjml.replace("{{BODY}}", bodyHtml);
    try {
      const { html } = mjml2html(mjmlString, { validationLevel: "soft" });
      return html;
    } catch {
      return null;
    }
  }, [template.mjml]);

  // Count key-contact tagged contacts in current selection (for hybrid label)
  const keyContactCount = contacts.filter(
    (c) =>
      selected.has(c.id) &&
      (c.tags || []).some((t) => t.toLowerCase() === "key-contact"),
  ).length;
  const bulkCount = selected.size - keyContactCount;

  const handleSendInvitations = async () => {
    const toInvite = contacts.filter((c) => selected.has(c.id));
    if (toInvite.length === 0) return;

    setSending(true);
    setError(null);
    try {
      let graphToken = null;
      if (
        sendMethod === "o365" ||
        sendMethod === "draft" ||
        sendMethod === "hybrid"
      ) {
        graphToken = await getGraphToken();
      }

      const compiledHtml = compileFinalHtml();

      const res = await sendZoomInvitations(event.id, {
        contacts: toInvite.map((c) => ({
          contactId: c.id,
          email: c.email,
          firstName: c.firstName,
          lastName: c.lastName,
          company: c.company,
          tags: c.tags || [],
        })),
        sendMethod,
        senderEmail:
          sendMethod === "o365" ||
          sendMethod === "draft" ||
          sendMethod === "hybrid"
            ? senderEmail
            : undefined,
        graphToken,
        emailHtml: compiledHtml,
        emailSubject: subject,
      });
      setResult(res);
      onInvited(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const previewWidth = previewMode === "mobile" ? 375 : 600;

  return (
    <>
      <div
        onClick={onClose}
        style={{ ...surfaces.overlay, zIndex: 109 }}
      />
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 110,
          pointerEvents: "none",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            width: "100%",
            maxWidth: step === "design" ? 920 : 600,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            pointerEvents: "auto",
            transition: "max-width 0.2s ease",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 24px",
              borderBottom: `1px solid ${colors.border}`,
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 18,
                  color: colors.text,
                  margin: 0,
                }}
              >
                {step === "design"
                  ? "Design Invitation"
                  : "Select Contacts & Send"}
              </h2>
              {/* Step indicator */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: fonts.body,
                    background:
                      step === "design" ? colors.accent : colors.accentDim,
                    color: step === "design" ? "#fff" : colors.accent,
                  }}
                >
                  1
                </div>
                <div
                  style={{ width: 16, height: 1, background: colors.border }}
                />
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: fonts.body,
                    background:
                      step === "send" ? colors.accent : colors.accentDim,
                    color: step === "send" ? "#fff" : colors.textSecondary,
                  }}
                >
                  2
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {step === "design" && (
                <div
                  style={{
                    display: "flex",
                    background: colors.bg,
                    borderRadius: 6,
                    padding: 2,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {[
                    { id: null, icon: Edit3, label: "Edit" },
                    { id: "desktop", icon: Monitor, label: "Desktop" },
                    { id: "mobile", icon: Smartphone, label: "Mobile" },
                  ].map((mode) => (
                    <button
                      key={mode.label}
                      onClick={() => setPreviewMode(mode.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "none",
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: fonts.body,
                        cursor: "pointer",
                        background:
                          previewMode === mode.id
                            ? colors.accentDim
                            : "transparent",
                        color:
                          previewMode === mode.id
                            ? colors.accent
                            : colors.textSecondary,
                      }}
                    >
                      <mode.icon size={13} /> {mode.label}
                    </button>
                  ))}
                </div>
              )}
              <X
                size={20}
                color={colors.textSecondary}
                style={{ cursor: "pointer" }}
                onClick={onClose}
              />
            </div>
          </div>

          {/* Step 1: Design */}
          {step === "design" && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Subject line */}
              <div
                style={{
                  padding: "12px 24px",
                  borderBottom: `1px solid ${colors.border}`,
                  flexShrink: 0,
                }}
              >
                <label style={{ ...labelStyle, marginBottom: 4 }}>
                  Subject Line
                </label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject line..."
                  style={inputStyle}
                />
              </div>

              {/* Editor or Preview */}
              <div style={{ flex: 1, overflow: "hidden" }}>
                {previewMode ? (
                  <div
                    style={{
                      height: "100%",
                      overflow: "auto",
                      display: "flex",
                      justifyContent: "center",
                      padding: "24px 20px",
                      background: "#f1f3f5",
                    }}
                  >
                    <div
                      style={{
                        width: previewWidth,
                        maxWidth: "100%",
                        background: "#ffffff",
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 16px",
                          background: "#f8f9fa",
                          borderBottom: "1px solid #e5e7eb",
                          fontSize: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            color: "#666",
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ fontWeight: 600, width: 40 }}>
                            Subj:
                          </span>
                          <span style={{ fontWeight: 600, color: "#111" }}>
                            {subject || "(no subject)"}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6, color: "#666" }}>
                          <span style={{ fontWeight: 600, width: 40 }}>
                            To:
                          </span>
                          <span>Sarah Chen &lt;s.chen@meridian.com&gt;</span>
                        </div>
                      </div>
                      <iframe
                        ref={iframeRef}
                        srcDoc={getCompiledEmailHtml()}
                        onLoad={handleIframeLoad}
                        sandbox="allow-same-origin"
                        title="Email Preview"
                        style={{
                          width: "100%",
                          minHeight: 400,
                          border: "none",
                          display: "block",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <EmailEditor
                    ref={editorRef}
                    content={template.defaultContent}
                    mergeTags={INVITE_MERGE_TAGS}
                    placeholder="Write your invitation email..."
                    compact
                  />
                )}
              </div>

              {/* Footer with Next button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 24px",
                  borderTop: `1px solid ${colors.border}`,
                  flexShrink: 0,
                }}
              >
                <Button variant="default" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setStep("send")}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  Select Contacts <ArrowRight size={14} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Send */}
          {step === "send" && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                padding: "16px 24px",
              }}
            >
              {/* Send method toggle */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Send Via</label>
                <div
                  style={{
                    display: "flex",
                    borderRadius: 6,
                    overflow: "hidden",
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {[
                    { id: "sendgrid", label: "FGF Brand Email" },
                    { id: "o365", label: "Personal O365" },
                    { id: "draft", label: "Draft in Mailbox" },
                    { id: "hybrid", label: "Hybrid Send" },
                  ].map((method, idx) => (
                    <button
                      key={method.id}
                      onClick={() => setSendMethod(method.id)}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: fonts.body,
                        border: "none",
                        borderLeft:
                          idx > 0 ? `1px solid ${colors.border}` : "none",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        background:
                          sendMethod === method.id
                            ? colors.accent
                            : "transparent",
                        color:
                          sendMethod === method.id
                            ? "#fff"
                            : colors.textSecondary,
                      }}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    marginTop: 4,
                  }}
                >
                  {sendMethod === "sendgrid"
                    ? "Sends from info@fordhamglobal.com via SendGrid"
                    : sendMethod === "o365"
                      ? "Sends immediately from the selected mailbox \u2014 appears in Sent Items"
                      : sendMethod === "draft"
                        ? "Creates drafts in the selected mailbox \u2014 review and send from Outlook"
                        : "Contacts tagged \u2018key-contact\u2019 get a personal O365 email \u2014 the rest go via SendGrid"}
                </div>
              </div>

              {/* Send-As dropdown (O365 / Draft only) */}
              {(sendMethod === "o365" ||
                sendMethod === "draft" ||
                sendMethod === "hybrid") && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Send As</label>
                  {loadingDelegates ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 0",
                        color: colors.textSecondary,
                        fontSize: 12,
                      }}
                    >
                      <Loader2
                        size={14}
                        className="spin"
                      />{" "}
                      Loading mailboxes...
                    </div>
                  ) : allowedSendAs.length <= 1 ? (
                    <div
                      style={{
                        fontSize: 13,
                        color: colors.text,
                        padding: "8px 0",
                      }}
                    >
                      {user?.userDetails || "your email"}
                    </div>
                  ) : (
                    <select
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      style={{
                        ...inputStyle,
                        cursor: "pointer",
                        appearance: "auto",
                      }}
                    >
                      {allowedSendAs.map((email) => (
                        <option key={email} value={email}>
                          {email}
                          {email === user?.userDetails?.toLowerCase()
                            ? " (you)"
                            : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {sendMethod === "hybrid" && selected.size > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    marginBottom: 10,
                    fontSize: 12,
                    color: colors.textSecondary,
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Mail size={12} color={colors.accent} />
                    <strong>{keyContactCount}</strong> key-contact
                    {keyContactCount !== 1 ? "s" : ""} via O365
                  </span>
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Send size={12} color={colors.textSecondary} />
                    <strong>{bulkCount}</strong> via SendGrid
                  </span>
                </div>
              )}

              {/* Load from audience list */}
              {audienceLists.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <select
                    defaultValue=""
                    onChange={(e) => { handleLoadList(e.target.value); e.target.value = ""; }}
                    disabled={loadingList}
                    style={{ ...inputStyle, flex: 1, fontSize: 11, padding: "6px 10px", cursor: "pointer" }}
                  >
                    <option value="" disabled>Load from audience list...</option>
                    {audienceLists.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                  {loadingList && <Loader2 size={14} color={colors.textSecondary} className="spin" />}
                </div>
              )}

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts..."
                style={{ ...inputStyle, marginBottom: 8 }}
              />

              <div
                style={{
                  display: "flex",
                  gap: 4,
                  marginBottom: 10,
                  flexWrap: "wrap",
                }}
              >
                {TYPE_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilterType(f.id)}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: fonts.body,
                      border: `1px solid ${filterType === f.id ? f.color || colors.accent : colors.border}`,
                      background:
                        filterType === f.id
                          ? f.color
                            ? f.color + "18"
                            : colors.accentDim
                          : "transparent",
                      color:
                        filterType === f.id
                          ? f.color || colors.accent
                          : colors.textSecondary,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {error && (
                <div style={{ ...surfaces.errorBanner, marginBottom: 8 }}>
                  {error}
                </div>
              )}

              {result && (
                <div
                  style={{
                    ...surfaces.successBanner,
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <CheckCircle2 size={13} />
                  {sendMethod === "draft"
                    ? `${result.sent} draft${result.sent !== 1 ? "s" : ""} created in ${senderEmail}`
                    : `${result.sent} invitation${result.sent !== 1 ? "s" : ""} sent`}
                  {result.total - result.sent > 0 &&
                    ` (${result.total - result.sent} skipped \u2014 already invited)`}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <button
                  onClick={selectAll}
                  style={{
                    background: "none",
                    border: "none",
                    color: colors.info,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: fonts.body,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {selected.size === filtered.length && filtered.length > 0
                    ? "Deselect All"
                    : "Select All"}
                </button>
                <span style={{ fontSize: 11, color: colors.textSecondary }}>
                  {filtered.length} contact{filtered.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  minHeight: 180,
                }}
              >
                {loading ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: colors.textSecondary,
                    }}
                  >
                    <Loader2
                      size={18}
                      className="spin"
                    />
                  </div>
                ) : filtered.length === 0 ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: colors.textSecondary,
                      fontSize: 13,
                    }}
                  >
                    No contacts found
                  </div>
                ) : (
                  filtered.map((c) => {
                    const isSelected = selected.has(c.id);
                    return (
                      <div
                        key={c.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 14px",
                          borderBottom: `1px solid ${colors.borderLight}`,
                          cursor: "pointer",
                          transition: "background 0.1s",
                          background: isSelected
                            ? "rgba(99,102,241,0.06)"
                            : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isSelected
                            ? "rgba(99,102,241,0.10)"
                            : colors.bgHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isSelected
                            ? "rgba(99,102,241,0.06)"
                            : "transparent";
                        }}
                        onClick={() => {
                          if (!sending) toggleSelect(c.id);
                        }}
                      >
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            flexShrink: 0,
                            border: `2px solid ${isSelected ? colors.info : colors.border}`,
                            background: isSelected
                              ? colors.info
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isSelected && (
                            <CheckCircle2 size={12} color="#fff" />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: colors.text,
                            }}
                          >
                            {[c.firstName, c.lastName]
                              .filter(Boolean)
                              .join(" ") || c.email}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: colors.textSecondary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.email}
                            {c.company ? ` — ${c.company}` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 16,
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Button
                    variant="default"
                    onClick={() => setStep("design")}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <ArrowLeft size={14} /> Edit Email
                  </Button>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>
                    {selected.size > 0
                      ? `${selected.size} selected`
                      : "Select contacts to invite"}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="default" onClick={onClose}>
                    {result ? "Done" : "Cancel"}
                  </Button>
                  {!result && (
                    <Button
                      variant="primary"
                      onClick={handleSendInvitations}
                      style={{
                        minWidth: 140,
                        opacity: selected.size === 0 || sending ? 0.5 : 1,
                      }}
                    >
                      {sending ? (
                        <>
                          <Loader2
                            size={14}
                            className="spin"
                          />
                          {sendMethod === "draft"
                            ? " Creating Drafts..."
                            : " Sending..."}
                        </>
                      ) : (
                        <>
                          <Send size={13} />{" "}
                          {sendMethod === "draft" ? "Create" : "Send"}{" "}
                          {selected.size > 0 ? selected.size + " " : ""}
                          {sendMethod === "draft" ? "Draft" : "Invitation"}
                          {selected.size !== 1 ? "s" : ""}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
